import ChartIcon from '@watershed/icons/components/Chart';
import HomeIcon from '@watershed/icons/components/Home';
import ReductionsIcon from '@watershed/icons/components/Reductions';
import ReportIcon from '@watershed/icons/components/Report';
import SetSquareIcon from '@watershed/icons/components/SetSquare';
import ShoppingBasketIcon from '@watershed/icons/components/ShoppingBasket';
import {
  routeForReductions,
  routeForBenchmarks,
  routeForDataGovernance,
  routeForFootprintConfiguration,
  routeForFootprintOverview,
  routeForFootprintProductLevelEmissions,
  routeForFootprintSuppliers,
  routeForFormalReporting,
  routeForHome,
  routeForMarketplace,
  routeForMarketplacePurchases,
  routeForMeasureIndex,
  routeForPortfolioBuilder,
  routeForReduxEnterprise,
  routeForRenewableEnergy,
  routeForReport,
  routeForSimulation,
  routeForClimateProgramTimeline,
  routeForEmissionsFactors,
  routeForTasks,
  routeForFootprintBiDrilldown,
  routeForMeasureHome,
  routeForDatasets,
  routeForSavedViewsLandingPage,
  routeForOrgStructure,
  routeForActivityDataOverview,
  routeForMethodologies,
  routeForReferenceData,
  routeForEmissionsModels,
} from '@watershed/shared-universal/dashboardRoutes';
import {
  GQFlags,
  GQPermissionType,
} from '@watershed/shared-universal/generated/graphql';
import isNotNullish from '@watershed/shared-universal/utils/isNotNullish';
import { useContext, useMemo } from 'react';
import { useAllFeatureFlags } from '../../../utils/FeatureFlag';
import { hasPermission } from '@watershed/shared-universal/utils/permissionUtils';
import {
  serializeSessionOverridesData,
  useSessionOverrideContext,
} from '../../../utils/SessionOverrideContext';
import { UserContext } from '../../../utils/UserContext';
import { NavItemData, NavSectionData, SideBarVariant } from '../utils';
import { hasPermissionToManageAnySupplier } from '@watershed/shared-frontend/utils/permissions/SupplierPermissionUtils';
import {
  HAS_ACCESS_TO_BENCHMARKS,
  checkAccessChecks,
  hasPermissionsForCleanPower,
  hasPermissionsForDataGovernance,
  hasPermissionsForFormalReporting,
  hasPermissionsForMeasurementProjects,
  hasPermissionsForMethodologyCustomization,
  hasPermissionsForReductionPlans,
} from '../../../utils/featureAccess';
import { hasFootprintReadyForRedux } from '../../../utils/redux';
import { FOOTPRINT_SNAPSHOT_DRILLDOWN_PARAM } from '@watershed/shared-universal/footprint/constants';
import pick from 'lodash/pick';
import ReportDisclosureIcon from '@watershed/icons/components/ReportDisclosure';
import { useGetEngagementTasksTypesAndStatus } from '../../footprint/suppliers/engagementTasks/useEngagementTasks';
import { sortMeasurementProjects } from '@watershed/shared-universal/utils/measurementUtils';
import { useLingui } from '@lingui/react';
import { msg } from '@lingui/macro';
import CalculatorIcon from '@watershed/icons/components/Calculator';

/**
 * Constructs our location object from a string route, assumes the route has no origin!
 */
export function makeNavItemLocationFromRoute(
  route: string
): NavItemData['location'] {
  return pick(new URL(window.location.origin + route), [
    'pathname',
    'search',
    'hash',
  ]);
}

export default function useSidebarStandardVariant(): SideBarVariant {
  // We have a lot of utility functions here, so just use the hook
  // to subscribe to locale changes, and use the t macro for actual strings.
  const { _ } = useLingui();
  const userContext = useContext(UserContext);
  const featureFlags = useAllFeatureFlags();
  const suppliersNavSection = useSuppliersNavSection();
  const reductionsNavSection = useReductionsNavSection();
  const summaryReportSubentry = useReportSummaryNavEntry();
  const tasksNavSection = useTasksNavSection();

  const navEntries = useMemo(() => {
    const isPipelinesEnabled = featureFlags.get(
      GQFlags.CalculationEnablePipelinesPrototype
    );

    const measurementsNavSection: NavSectionData = {
      name: _(msg`Measurements`),
      icon: SetSquareIcon,
      subentries: [],
      location: { pathname: routeForMeasureHome() },
      disabled: !userContext.watershedPlanIncludesMeasurement,
      hasPermission: hasPermission(
        userContext.permissions,
        [GQPermissionType.ManageDatasource],
        { allowAnyObject: true }
      ),
    };
    const measurementPageTitle = (() => {
      if (userContext.projects.length === 0) {
        return _(msg`Active measurement`);
      }
      const hasActiveProject = userContext.projects.some(
        (project) => project.active
      );
      return hasActiveProject
        ? _(msg`Active measurement`)
        : _(msg`Latest measurement`);
    })();

    const projectId =
      userContext.projects.length > 0
        ? sortMeasurementProjects(userContext.projects)[0].id
        : undefined;

    measurementsNavSection.subentries.push({
      name: measurementPageTitle,
      location: makeNavItemLocationFromRoute(
        routeForMeasureIndex({ projectId })
      ),
      hasPermission: hasPermissionsForMeasurementProjects(userContext),
    });

    measurementsNavSection.subentries.push({
      name: _(msg`Datasets`),
      location: makeNavItemLocationFromRoute(routeForDatasets()),
      hasPermission:
        hasPermissionsForMeasurementProjects(userContext) &&
        hasPermission(
          userContext.permissions,
          [GQPermissionType.ManageDatasource],
          {
            allowAnyObject: true,
          }
        ),
    });

    if (
      featureFlags.get(GQFlags.ActivityDataPage) ||
      featureFlags.get(GQFlags.ShowActivityDataInDataReview) ||
      isPipelinesEnabled
    ) {
      measurementsNavSection.subentries.push({
        name: _(msg`Activity data`),
        location: makeNavItemLocationFromRoute(routeForActivityDataOverview()),
        hasPermission: hasPermissionsForMeasurementProjects(userContext),
      });
    }

    if (featureFlags.get(GQFlags.CalculationMethodologyCustomizationPage)) {
      measurementsNavSection.subentries.push({
        name: _(msg`Emissions factors`),
        location: makeNavItemLocationFromRoute(routeForEmissionsFactors()),
        hasPermission: hasPermissionsForMethodologyCustomization(userContext),
      });
    }

    if (!featureFlags.get(GQFlags.DisableDataGovernancePage)) {
      measurementsNavSection.subentries.push({
        name: _(msg`Data governance`),
        location: makeNavItemLocationFromRoute(routeForDataGovernance()),
        hasPermission: hasPermissionsForDataGovernance(userContext),
      });
    }

    const hasPermissionsForCalculateSection =
      hasPermissionsForMeasurementProjects(userContext) &&
      hasPermission(
        userContext.permissions,
        [GQPermissionType.ManageDatasource],
        {
          allowAnyObject: true,
        }
      );
    const calculateSection: NavSectionData = {
      name: _(msg`Calculation`),
      icon: CalculatorIcon,
      subentries: [
        {
          name: _(msg`Methodologies`),
          location: makeNavItemLocationFromRoute(routeForMethodologies()),
          hasPermission: hasPermissionsForCalculateSection,
        },
        {
          name: _(msg`Reference data`),
          location: makeNavItemLocationFromRoute(routeForReferenceData()),
          hasPermission: hasPermissionsForCalculateSection,
        },
        {
          name: _(msg`Emissions models`),
          location: makeNavItemLocationFromRoute(routeForEmissionsModels()),
          hasPermission: hasPermissionsForCalculateSection,
        },
      ],
      location: { pathname: routeForMethodologies() },
      hasPermission: hasPermissionsForCalculateSection,
    };

    const footprintNavSection: NavSectionData = {
      name: _(msg`Footprint`),
      icon: ChartIcon,
      subentries: [],
      location: { pathname: '' },
      hasPermission:
        hasPermission(userContext.permissions, [
          GQPermissionType.ViewFootprintDetail,
        ]) || hasPermissionToManageAnySupplier(userContext.permissions),
    };

    const drilldownSearchParams = new URLSearchParams();
    if (userContext.latestPublishedFootprintVersion) {
      drilldownSearchParams.append(
        FOOTPRINT_SNAPSHOT_DRILLDOWN_PARAM,
        userContext.latestPublishedFootprintVersion
      );
    }
    const drilldownNavSection: NavItemData = {
      name: _(msg`Drilldown`),
      location: makeNavItemLocationFromRoute(
        featureFlags.get(GQFlags.SavedViewsLandingPage)
          ? routeForSavedViewsLandingPage()
          : routeForFootprintBiDrilldown()
      ),
      hasPermission: hasPermission(userContext.permissions, [
        GQPermissionType.ViewFootprintDetail,
      ]),
    };
    const portfolioBuilderSection: NavItemData = {
      name: _(msg`Portfolios`),
      location: makeNavItemLocationFromRoute(routeForPortfolioBuilder()),
      hasPermission: hasPermission(userContext.permissions, [
        GQPermissionType.ManageMarketplacePurchases,
      ]),
    };
    const renewableEnergyNavSection: NavItemData = {
      name: _(msg`Clean power`),
      location: makeNavItemLocationFromRoute(routeForRenewableEnergy()),
      hasPermission: hasPermissionsForCleanPower(userContext),
    };
    const productLevelEmissionsNavSection: NavItemData = {
      name: _(msg`Products and materials`),
      location: makeNavItemLocationFromRoute(
        routeForFootprintProductLevelEmissions()
      ),
      hasPermission: hasPermission(userContext.permissions, [
        GQPermissionType.ViewFootprintDetail,
      ]),
    };

    const changelogNavSection: NavItemData = {
      name: _(msg`Footprint configuration`),
      location: makeNavItemLocationFromRoute(routeForFootprintConfiguration()),
      hasPermission: hasPermission(userContext.permissions, [
        GQPermissionType.ViewFootprintDetail,
      ]),
    };

    if (userContext.footprintInterval) {
      footprintNavSection.location.pathname = routeForFootprintOverview();
      footprintNavSection.subentries = [
        featureFlags.get(GQFlags.HideOverviewNav)
          ? null
          : {
              name: _(msg`Overview`),
              location: makeNavItemLocationFromRoute(
                routeForFootprintOverview()
              ),
              // Hack to hide overview tab with a flag
              hasPermission: hasPermission(userContext.permissions, [
                GQPermissionType.ViewFootprintDetail,
              ]),
            },
        drilldownNavSection,
        featureFlags.get(GQFlags.FootprintProductLevelEmissions)
          ? productLevelEmissionsNavSection
          : null,
        suppliersNavSection,
        changelogNavSection,
      ].filter(isNotNullish);
    } else {
      footprintNavSection.location = changelogNavSection.location;
      footprintNavSection.subentries = [changelogNavSection];
    }

    const marketplaceNavSection: NavSectionData = {
      name: _(msg`Marketplace`),
      location: makeNavItemLocationFromRoute(routeForMarketplace()),
      icon: ShoppingBasketIcon,
      disabled: false,
      hasPermission: hasPermission(userContext.permissions, [
        GQPermissionType.ManageMarketplacePurchases,
      ]),
      subentries: [
        {
          name: _(msg`Explore`),
          location: makeNavItemLocationFromRoute(routeForMarketplace()),
          hasPermission: hasPermission(userContext.permissions, [
            GQPermissionType.ManageMarketplacePurchases,
          ]),
        },
        renewableEnergyNavSection,
        featureFlags.get(GQFlags.MarketplacePortfolioBuilder)
          ? portfolioBuilderSection
          : null,
        {
          name: _(msg`Purchases`),
          location: makeNavItemLocationFromRoute(
            routeForMarketplacePurchases()
          ),
          hasPermission: hasPermission(userContext.permissions, [
            GQPermissionType.ManageMarketplacePurchases,
          ]),
        },
      ].filter(isNotNullish),
    };

    const reportingNavSection: NavSectionData = (() => {
      const subentries: ReadonlyArray<NavItemData> = [
        {
          name: _(msg`Disclosures & reports`),
          tooltip: _(
            msg`Create audit-ready reports and disclosures tailored to your needs.`
          ),
          location: makeNavItemLocationFromRoute(routeForFormalReporting()),
          hasPermission: hasPermissionsForFormalReporting(userContext),
        },
        summaryReportSubentry,
      ];

      // This isn't the cleanest, but while we have both program summary and the
      // report section under one header, this seems like the best way to figure
      // out where clicking "Reporting" should take you.
      const toplinePath = subentries.find(
        (subentry) => subentry.hasPermission && !subentry.disabled
      )?.location.pathname;

      return {
        name: _(msg`Reporting`),
        location: {
          pathname: toplinePath ?? routeForFormalReporting(),
        },
        disabled: !toplinePath || !userContext.watershedPlanIncludesReporting,
        hasPermission: hasPermission(userContext.permissions, [
          GQPermissionType.ViewEmployeeReport,
          GQPermissionType.ViewFootprintDetail,
        ]),
        icon: ReportIcon,
        subentries: [
          {
            name: _(msg`Disclosures & reports`),
            tooltip: _(
              msg`Create audit-ready reports and disclosures tailored to your needs.`
            ),
            location: makeNavItemLocationFromRoute(routeForFormalReporting()),
            hasPermission: hasPermission(userContext.permissions, [
              GQPermissionType.ViewFootprintDetail,
            ]),
          },
          summaryReportSubentry,
        ].filter(isNotNullish),
      };
    })();

    const benchmarksAccessCheck = checkAccessChecks(
      HAS_ACCESS_TO_BENCHMARKS,
      userContext,
      featureFlags
    );

    const hasOrgStructure = featureFlags.get(GQFlags.OrgStructure);

    const homeNavSection: NavSectionData = {
      name: _(msg`Home`),
      location: {
        pathname: routeForHome(),
      },
      icon: HomeIcon,
      subentries: [
        {
          name: _(msg`Timeline`),
          location: makeNavItemLocationFromRoute(
            routeForClimateProgramTimeline()
          ),
          hasPermission: true,
        },
        benchmarksAccessCheck.hasAccess
          ? {
              name: _(msg`Benchmarks`),
              location: makeNavItemLocationFromRoute(routeForBenchmarks()),
              hasPermission: true,
            }
          : null,
        hasOrgStructure
          ? {
              name: _(msg`Organizational structure`),
              location: makeNavItemLocationFromRoute(routeForOrgStructure()),
              hasPermission: hasPermission(userContext.permissions, [
                GQPermissionType.Admin,
              ]),
            }
          : null,
      ].filter(isNotNullish),
      hasPermission: hasPermission(userContext.permissions, [
        GQPermissionType.ViewFootprintDetail,
      ]),
    };

    const navSections: ReadonlyArray<NavSectionData | null> = [
      homeNavSection,
      measurementsNavSection,
      isPipelinesEnabled ? calculateSection : null,
      footprintNavSection,
      reductionsNavSection,
      marketplaceNavSection,
      reportingNavSection,
      tasksNavSection,
    ];

    return navSections.filter(isNotNullish).map((entry) => ({
      ...entry,
      disabled:
        entry.disabled ??
        (entry.subentries.length > 0 &&
          entry.subentries.every(
            (subentry) => subentry.disabled || !subentry.hasPermission
          )),
      location:
        entry.name === _(msg`Home`) || entry.name === _(msg`Measurements`)
          ? entry.location
          : entry.subentries.filter(
              (subentry) => subentry.hasPermission && !subentry.disabled
            )[0]?.location ?? entry.location,
      subentries: entry.disabled ? [] : entry.subentries,
    }));
  }, [
    _,
    userContext,
    featureFlags,
    reductionsNavSection,
    tasksNavSection,
    suppliersNavSection,
    summaryReportSubentry,
  ]);
  return { navEntries };
}

export function useSuppliersNavSection(): NavItemData | null {
  const { _ } = useLingui();
  const userContext = useContext(UserContext);
  const featureFlags = useAllFeatureFlags();

  return featureFlags.get(GQFlags.FootprintSuppliersPage)
    ? {
        name: _(msg`Supply chain`),
        location: makeNavItemLocationFromRoute(routeForFootprintSuppliers()),
        hasPermission: hasPermissionToManageAnySupplier(
          userContext.permissions
        ),
      }
    : null;
}

function useTasksNavSection() {
  const { _ } = useLingui();
  const userContext = useContext(UserContext);
  const featureFlags = useAllFeatureFlags();
  const { engagementTasks } = useGetEngagementTasksTypesAndStatus();

  if (
    !hasPermission(userContext.permissions, [
      GQPermissionType.ManageDisclosures,
    ])
  ) {
    return null;
  }
  if (!featureFlags.get(GQFlags.AssetManagerCorporatePortCoTasksPage)) {
    return null;
  }

  if (engagementTasks.length === 0) {
    return null;
  }

  const incompleteTasks = engagementTasks.filter(
    (task) => task.submittedAt === null
  );

  return {
    name: _(msg`Network tasks`),
    location: makeNavItemLocationFromRoute(routeForTasks()),
    hasPermission: hasPermission(userContext.permissions, [
      GQPermissionType.ManageDisclosures,
    ]),
    chip: incompleteTasks.length > 0 ? incompleteTasks.length.toString() : null,
    icon: ReportDisclosureIcon,
    subentries: [],
  };
}

export function useReductionsNavSection() {
  const { _ } = useLingui();
  const userContext = useContext(UserContext);
  const featureFlags = useAllFeatureFlags();

  // If all plans exists, it needs to be the first thing in the list, otherwise our active link state breaks - this is because of path matching (/redux vs /redux/simulation)
  const simulationNavSection: NavItemData = {
    name: _(msg`Simulate`),
    location: makeNavItemLocationFromRoute(routeForSimulation()),
    hasPermission: hasPermission(userContext.permissions, [
      GQPermissionType.ViewFootprintDetail,
    ]),
  };
  const simulationSubentries = featureFlags.get(
    GQFlags.SimulationEnableSavingScenarios
  )
    ? [simulationNavSection]
    : [];

  const subentries = [
    ...simulationSubentries,
    {
      name: _(msg`Plans`),
      location: makeNavItemLocationFromRoute(routeForReduxEnterprise()),
      hasPermission: hasPermissionsForReductionPlans(userContext),
    },
  ];

  const footprintReadyForRedux = hasFootprintReadyForRedux(
    userContext.orgFiscalYearStartMonth,
    userContext.footprintInterval
  );
  return {
    name: _(msg`Reduction plans`),
    location: makeNavItemLocationFromRoute(routeForReductions()),
    disabled: !footprintReadyForRedux,
    tooltip: !footprintReadyForRedux
      ? _(
          msg`You need a published footprint with one full year of data to use the reductions tool.`
        )
      : undefined,
    hasPermission: hasPermissionsForReductionPlans(userContext),
    icon: ReductionsIcon,
    subentries,
  };
}

export function useReportSummaryNavEntry() {
  const { _ } = useLingui();
  const userContext = useContext(UserContext);
  const { data: sessionOverridesData } = useSessionOverrideContext();

  const sessionOverridesDataSearch = new URLSearchParams(
    serializeSessionOverridesData(sessionOverridesData)
  );
  return {
    name: _(msg`Program summary`),
    tooltip: _(
      msg`A high-level summary of your emissions and climate plan for informal sharing.`
    ),
    location: {
      ...makeNavItemLocationFromRoute(routeForReport('summary')),
      search: sessionOverridesDataSearch.toString(),
    },
    disabled:
      userContext.footprintInterval === null ||
      userContext.employeeReportId === null,
    hasPermission: hasPermission(userContext.permissions, [
      GQPermissionType.ViewEmployeeReport,
    ]),
    openInNewTab: true,
  };
}
